import React, { useState, useEffect } from 'react';
import Typography from '../Text/Typography';
import Tag from '../Tag/Tag';
import Colors from '../Colors/Colors';
import ContentfulImage from '../ContentfulImage/ContentfulImage';
import { API_BASE_URL } from '../../config/api';
import './ProductDetailContent.css';

const capitalizeWords = (str: string) => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export interface ProductDetailProduct {
  product_id: number | string;
  product_name: string;
  brand: string;
  category?: string;
  type?: string;
  image_url?: string;
  actives?: string;
  activeIngredients?: string;
  description?: string;
  ingredients?: string;
}

interface ProductDetailContentProps {
  product: ProductDetailProduct;
}

const ProductDetailContent: React.FC<ProductDetailContentProps> = ({ product: productProp }) => {
  const [product, setProduct] = useState<ProductDetailProduct>(productProp);

  // Always fetch full product from Contentful via API so we have actives/description (parent may not pass them)
  useEffect(() => {
    setProduct(productProp);
    const id = productProp.product_id != null ? String(productProp.product_id).trim() : '';
    if (!id) return;

    let cancelled = false;
    fetch(`${API_BASE_URL}/api/products/${encodeURIComponent(id)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((full) => {
        if (cancelled || !full) return;
        const activesVal = full.actives ?? full.activeIngredients ?? '';
        const activesStr = typeof activesVal === 'string'
          ? activesVal.trim()
          : Array.isArray(activesVal)
            ? (activesVal as unknown[]).map((a) => (typeof a === 'string' ? a : String(a))).join(', ').trim()
            : '';
        const descVal = full.description ?? full.ingredients ?? '';
        const descStr = typeof descVal === 'string' ? descVal.trim() : '';
        setProduct((prev) => ({
          ...prev,
          actives: activesStr || prev.actives || prev.activeIngredients || '',
          activeIngredients: activesStr || prev.activeIngredients || prev.actives || '',
          description: descStr || prev.description || '',
          ingredients: descStr || prev.ingredients || prev.description || '',
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productProp.product_id]);

  const productType = product.type || product.category || '';
  const rawActives = product.actives ?? product.activeIngredients ?? '';
  const activeIngredients = (typeof rawActives === 'string' ? rawActives : Array.isArray(rawActives) ? (rawActives as string[]).join(', ') : '').trim().replace(/^(null|undefined)$/i, '');
  const rawInactive = product.description ?? product.ingredients ?? '';
  const inactiveIngredients = (typeof rawInactive === 'string' ? rawInactive : '').trim().replace(/^(null|undefined)$/i, '');

  return (
    <div className="product-detail-content-shared">
      {/* 1. Product Image (large, top) */}
      <div className="product-detail-content-image">
        {product.image_url ? (
          <ContentfulImage
            src={product.image_url}
            alt={product.product_name}
            width={320}
            height={320}
            fit="fill"
            quality={80}
            format="webp"
            responsive={true}
          />
        ) : (
          <div className="product-detail-content-image-placeholder" aria-hidden />
        )}
      </div>

      {/* 2. Brand */}
      <Colors variant="dark-gray">
        <Typography variant="body-small-bold" className="product-detail-content-brand">
          {product.brand}
        </Typography>
      </Colors>

      {/* 3. Product Name */}
      <Typography variant="h2" className="product-detail-content-name">
        {product.product_name}
      </Typography>

      {/* 4. Product Type */}
      {productType && (
        <div className="product-detail-content-type">
          <Tag>{capitalizeWords(productType)}</Tag>
        </div>
      )}

      {/* 5. Active Ingredients - hide if empty */}
      {activeIngredients.length > 0 && (
        <>
          <hr className="product-detail-content-divider" />
          <Typography variant="body-small-bold" className="product-detail-content-section-label">
            Active Ingredients
          </Typography>
          <Typography variant="body" className="product-detail-content-section-value">
            {activeIngredients}
          </Typography>
        </>
      )}

      {/* 6. Inactive Ingredients - hide if empty */}
      {inactiveIngredients.length > 0 && (
        <>
          <hr className="product-detail-content-divider" />
          <Typography variant="body-small-bold" className="product-detail-content-section-label">
            Inactive Ingredients
          </Typography>
          <Typography variant="body" className="product-detail-content-section-value">
            {inactiveIngredients}
          </Typography>
        </>
      )}
    </div>
  );
};

export default ProductDetailContent;
